import Feature from "ol/Feature";
import Geometry from "ol/geom/Geometry";
import Point from "ol/geom/Point";
import Circle from 'ol/geom/Circle';
import { getCenter } from "ol/extent"
import { transform } from "ol/proj";
import { SwatchesPicker } from "react-color";

import { useState, useEffect } from "react";
import { Popup, PopupProps } from "lib/classes/popup";
import { getLocaleString } from "lib/localization/vendor";
import { assetURL } from "lib/utilities/asset";
import { 
	readonlyAttributes,
	getCenterCoords,
	formatFeature
} from "lib/utilities/feature";

import MapToolMode from "lib/enums/maptoolmode";
import AddAttributePopup from "views/popups/AddAttribute";
import Attributes from "lib/utilities/attributes";

// Popups
import ChangelogPopup from 'views/popups/Changelog';

import "styles/css/FeatureEditPopup.scss";

var _newValues:any;

const FeatureEditPopup = new Popup({
	title: function() {
		return `${getLocaleString("metadata")}`;
	},
	style: {
		maxWidth: 550
	},
	onClose: function() {
		this.get("reset")?.();
	},
	closeable: false,
	render: function() {
		const mapConfig:any = this.get("map-config");
		
		const [features, setFeatures] = useState<Feature<Geometry>[]>(this.get("features") || []);
		const [newValues, setNewValues] = useState<any>({});
		const [showReadOnly, setShowReadOnly] = useState<boolean>(false);
		
		useEffect(() => {
			_events.on("tool-mode-changed", (toolMode:MapToolMode) => {
				if(toolMode != MapToolMode.Select && _ui.isPopupOpen(this))
					_ui.closePopupOf(this);
			});
			_events.on("selected-features", (features:Feature<Geometry>[]) => {
				/*if(features.length == 0)
					return _ui.closePopupOf(this);*/
				
				setFeatures([...features]);
				setNewValues({});
			});
		}, []);
		
		useEffect(() => {
			this.set("features", features);
			
			const onFeatureModify:Function = (feature:Feature<Geometry>) => {
				refresh();
			};
			
			_events.on("feature-modify", onFeatureModify);
			
			return () => {
				_events.un("feature-modify", onFeatureModify);
			}
		}, [features]);
		
		useEffect(() => {
			this.set("reset", () => setNewValues({}));
			
			_newValues = newValues;
		}, [newValues]);
		
		const isValidValue = (value:any):boolean => {
			return typeof(value) == "string" || typeof(value) == "number" || typeof(value) == "boolean";
		}
		const isRasterImageEdit = ():boolean => {
			let res:boolean = true;
			
			features.forEach((feature:Feature<Geometry>) => {
				if(!feature.get("rasterimg"))
					res = false;
			});
			
			return res;
		}
		const shouldShowAttributes = ():boolean => {
			return true; //!isRasterImageEdit();
		}
		const showChangeLog = () => {
			ChangelogPopup.set('feature', features[0]);
			
			//_ui.closePopupOf(this);
			_ui.showPopup(ChangelogPopup);
		}
		const getProperty = (name:string, fromSource:boolean = false):any => {
			if(newValues[name] !== undefined && !fromSource)
				return newValues[name];
			
			let value:any = "";
			let hasValue:boolean = false;
			
			features.forEach((feature:Feature<Geometry>) => {
				let propValue = feature.get(name);
				
				switch(name) {
					case "$LAT":
						propValue = getCenterCoords(feature)[0].toFixed(6);
					break;
					case "$LONG":
						propValue = getCenterCoords(feature)[1].toFixed(6);
					break;
					case "$GTYPE":
						propValue = feature.getGeometry().getType();
					break;
					case "$SCALE":
						propValue = 100;
					break;
				}
				
				if(propValue !== undefined && isValidValue(propValue)) {
					if(hasValue && propValue != value)
						value = "(Multiple)";
					else {
						value = propValue;
						hasValue = true;
					}
				}
			});
			
			return value;
		}
		const setProperty = (name:string, value:any) => {
			features.forEach((feature:Feature<Geometry>) => {
				switch(name) {
					case "$LAT": 
					case "$LONG": 
					{
						if(isNaN(value))
							return _ui.showError("Invalid co-ordinates!");
						
						// Get center coords in lat long
						const coordsLatLong:Array<number> = getCenterCoords(feature);
						
						// Calculate the new lat long coordinates
						const newCoords:Array<number> = [
							(name == "$LAT" ? value : coordsLatLong[0]),
							(name == "$LONG" ? value : coordsLatLong[1])
						];
						
						// Convert those into view coordinates
						const setCoords:Array<number> = transform(
							newCoords,
							"EPSG:4326",
							mapConfig.epsgCodeView
						);
						
						// Get the current view coordinates
						const coordsView:Array<number> = getCenter(feature.getGeometry().getExtent());
						
						if(name == "$LAT")
							feature.getGeometry().translate(setCoords[0] - coordsView[0], 0);
						else
						if(name == "$LONG")
							feature.getGeometry().translate(0, setCoords[1] - coordsView[1]);
						
						// Update raster image
						if(feature.get("rasterimg"))
							_events.invoke("raster-transform", feature, {}, true);
						
						return;
					}
					case "$SCALE": {
						if(isNaN(value))
							return _ui.showError("Invalid scale!");
							
						feature.getGeometry().scale(value / 100);
						
						_events.invoke("feature-modify", {features: [feature]});
						
						// Update raster image
						if(feature.get("rasterimg"))
							_events.invoke("raster-transform", feature, {}, true);
							
						
						return;
					}
				}
				
				if(value !== undefined)
					feature.set(name, value);
				else
					feature.unset(name);
			});
		}
		const setPropertyState = (name:string, value:any) => {
			const nv:any = JSON.parse(JSON.stringify(_newValues));
			
			nv[name] = value;
			
			setNewValues(nv);
			_newValues = nv;
		}
		const saveValues = () => {
			for(let name in _newValues) {
				const value:any = _newValues[name];
				
				setProperty(name, value);
			}
			
			syncAll();
		}
		const syncAll = () => {
			_events.invoke('sync-annotationtool');
			_events.invoke('sync-drawtool');
		}
		const saveButton = () => {
			saveValues();
			_ui.closePopupOf(this);
		}
		const showAddAttributePopup = () => {
			AddAttributePopup.set("features", features);
			AddAttributePopup.set("onSaved", () => refresh()); // Refresh table
			
			_ui.showPopup(AddAttributePopup);
		}
		const refresh = () => {
			setFeatures([...features]);
		}
		
		const drawAttributesSection = () => {
			const rows:any[] = drawAttributesTable({readonly: false});
			const rows2:any[] = drawAttributesTable({readonly: true});
			
			return (
				<div className="fe-section">
					{
						/*
						<div style={{marginBottom: 15}}>
							<div className="fe-section--tool">
								<button onClick={() => showAddAttributePopup()}>
									{getLocaleString("addattribute")}
								</button>
							</div>
							
							<div style={{paddingTop: 10}}>
								<b>Data ({rows.length + rows2.length})</b>
							</div>
						</div>
						<hr />
						*/
					}
					<div className="fe-attributes">
						{showReadOnly ? [...rows, ...rows2] : rows}
						
						{features.length <= 1 && 
							<div onClick={() => showChangeLog()} style={{cursor: 'pointer', float: 'left'}}>
								🗒️ {getLocaleString('changelog')}
							</div>
						}
						<div onClick={() => setShowReadOnly(!showReadOnly)} style={{cursor: 'pointer', float: 'right'}}>
							{showReadOnly ? `⬆️ ${getLocaleString('hidereadonly')}` : `⬇️ ${getLocaleString('showreadonly')}`}
						</div>
					</div>
					<hr />
				</div>
			);
		}
		const drawAttributesTable = (options:any) => {
			const drawReadOnly:boolean = options.readonly ?? false;
			
			const rows:any[] = [];
			const done:Array<string> = [];
			
			features.forEach((feature:Feature<Geometry>) => {
				const attributes:any = Object.entries(feature.getProperties());
				
				attributes.sort((a:any, b:any) => {
					const isInReadOnlyA:boolean = readonlyAttributes.includes(a[0]);
					const isInReadOnlyB:boolean = readonlyAttributes.includes(b[0]);
					
					if (isInReadOnlyA && !isInReadOnlyB)
						return 1;
						
					if (!isInReadOnlyA && isInReadOnlyB)
						return -1;
					
					return 0;
				});
				attributes.sort((a:any, b:any) => {
					const attr1:any = Attributes[a[0]];
					const attr2:any = Attributes[b[0]];
					
					if(attr1?.index < attr2?.index)
						return 1;
					
					if(attr1?.index > attr2?.index)
						return -1;
						
					return 0;
				});
				
				attributes.forEach((attribute:any) => {
					const name:string = attribute[0];
					const currentValue:any = attribute[1];
					
					const readonly:boolean = readonlyAttributes.includes(name);
					
					if(done.includes(name))
						return;
					
					if(!isValidValue(currentValue))
						return;
					
					if(readonly != drawReadOnly)
						return;
					
					const deleteMethod:Function = () => {
						if(readonly)
							return _ui.showError(getLocaleString("attrnodel"));
							
						_ui.showConfirm(getLocaleString("attrdelcon"), () => {
							feature.unset(name);
							syncAll();
							refresh();
						});
					}
					
					rows.push(
						<div className="fe-field" key={rows.length + (drawReadOnly ? 1000 : 0)}>
							<div className="fe-label">
								{name}
							</div>
							<div className="fe-input">
								{drawInputField(name, readonly)}
								{
									/*
									<button onClick={() => deleteMethod()} className="fe-delete">
										🗑️
									</button>
									*/
								}
							</div>
						</div>
					);
					
					done.push(name);
				});
			});
			
			return rows;
		}
		const drawInputField = (name:string, readonly:boolean = false) => {
			const value:any = getProperty(name);
			const attribute:any = Attributes[name];
			
			const onChange:any = (e:any) => {
				setPropertyState(name, e.target.value);
				attribute?.autoSave && saveValues();
			};
			
			switch(attribute?.type) {
				case "range":
					return (
						<input 
							type="range" 
							min={attribute.range?.min}
							max={attribute.range?.max}
							step={attribute.step}
							value={value} 
							onChange={onChange}
							readOnly={readonly}
						/>
					)
				case "number":
					return (
						<input 
							type="number" 
							min={attribute.range?.min}
							max={attribute.range?.max}
							value={value} 
							onChange={onChange}
							readOnly={readonly}
						/>
					)
				case "color":
					return (
						<div style={{width: '100%'}}>
							<SwatchesPicker
								color={value}
								onChange={ (color:any) => onChange({target: {value: color.hex}}) }
								height={150}
							/>
						</div>
					)
				case "textarea":
					return (
						<textarea 
							rows={attribute.rows || 4} 
							cols={attribute.cols || 40}
							value={value} 
							onChange={onChange}
							readOnly={readonly}
							style={{resize: 'vertical'}}
						/>
					)
				default:
					return (
						<input 
							type="text" 
							value={value} 
							onChange={onChange}
							readOnly={readonly}
						/>
					)
			}
		}
		
		if(features.length < 1) {
			return (
				<>
					<p>No features selected</p>
				</>
			)
		}
		
		const drawGeometry = () => {
			if(getProperty('annotation'))
				return;
				
			return (
				<div className="fe-section">
					<b>Geometry</b>
					<hr />
					{drawType()}
					{drawPositioning()}
					{drawScale()}
				</div>
			)
		}
		const drawType = () => {
			return (
				<div className="fe-field">
					<div className="fe-label">
						Type
					</div>
					<div className="fe-input">
						<input 
							type="text" 
							value={getProperty("$GTYPE")} 
							readOnly={true}
						/>
					</div>
				</div>
			)
		}
		const drawPositioning = () => {
			return (
				<div className="fe-field">
					<div className="fe-label">
						Center
					</div>
					<div className="fe-input">
						<input 
							type="number" 
							placeholder="Longitude"
							step={0.000001}
							value={getProperty("$LONG")} 
							onChange={(e:any) => {
								setPropertyState("$LONG", e.target.value);
								saveValues();
							}}
						/>
					</div>
					<div className="fe-input">
						<input 
							type="number" 
							placeholder="Latitude"
							step={0.000001}
							value={getProperty("$LAT")}
							onChange={(e:any) => {
								setPropertyState("$LAT", e.target.value);
								saveValues();
							}}
						/>
					</div>
				</div>
			)
		}
		const drawScale = () => {
			if(getProperty("$GTYPE") == 'Point') 
				return;
			
			return (
				<div className="fe-field">
					<div className="fe-label">
						Scale
					</div>
					<div className="fe-input">
						<input 
							type="number" 
							placeholder="Scale"
							value={getProperty("$SCALE")} 
							onChange={(e:any) => setPropertyState("$SCALE", e.target.value)}
						/> %
					</div>
				</div>
			)
		}
		
		return (
			<>
				{drawGeometry()}
				
				{shouldShowAttributes() && drawAttributesSection()}
				
				<div style={{height: 30}}></div>
				
				<div className="fe-submit">
					<button onClick={() => this.closeMe()} style={{backgroundColor: '#ccc', color: 'black'}}>
						{getLocaleString("cancel")}
					</button>
					<button onClick={() => saveButton()}>
						☑️ {getLocaleString("save")}
					</button>
				</div>
			</>
		)
	}
});

export default FeatureEditPopup;